import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { auth as authApi } from "@/lib/api";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("tabs"); // tabs | forgot | reset
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetToken, setResetToken] = useState(searchParams.get("token") || "");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(null); // holds reset_token for dev

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const d = new FormData(e.target);
    try {
      await login(d.get("email"), d.get("password"));
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const d = new FormData(e.target);
    try {
      await register(d.get("email"), d.get("username"), d.get("password"));
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authApi.forgotPassword(forgotEmail);
      setResetEmailSent(res.data);
      toast.success("Reset token generated");
    } catch {
      toast.error("Failed to request reset");
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await authApi.resetPassword(resetToken, newPassword);
      toast.success("Password reset! Please sign in.");
      setView("tabs");
      setResetToken("");
      setNewPassword("");
      setResetEmailSent(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-slate-50 via-white to-slate-100/50">
      <div className="w-full max-w-md p-8">
        <button
          onClick={() => navigate("/")}
          className="flex items-center text-sm text-slate-500 hover:text-slate-900 mb-8 transition-colors font-body"
          data-testid="auth-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to home
        </button>

        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
              <path d="M2 4L8 2L14 4V12L8 14L2 12V4Z" stroke="white" strokeWidth="1.5" fill="none" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading">DiagramFoundry</h1>
        </div>
        <p className="text-slate-500 mb-8 font-body text-sm">Create powerful diagrams with code and AI</p>

        {/* ---- FORGOT PASSWORD ---- */}
        {view === "forgot" && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 font-heading mb-1">Forgot Password</h2>
              <p className="text-sm text-slate-500 font-body">Enter your email to receive a reset token.</p>
            </div>

            {!resetEmailSent ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email" required placeholder="you@example.com"
                    value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? "Sending..." : "Request Reset Token"}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 space-y-1">
                  <p className="font-semibold">Reset token generated</p>
                  <p className="font-mono text-xs break-all bg-white border border-green-200 rounded p-2 select-all" data-testid="reset-token-display">
                    {resetEmailSent.reset_token}
                  </p>
                  <p className="text-xs text-green-600">Copy this token and use it below to set your new password.</p>
                </div>
                <Button
                  className="w-full"
                  onClick={() => { setResetToken(resetEmailSent.reset_token); setView("reset"); }}
                  data-testid="use-token-btn"
                >
                  Continue to Reset Password
                </Button>
              </div>
            )}

            <button onClick={() => setView("tabs")} className="text-xs text-slate-400 hover:text-slate-700 transition-colors">
              Back to Sign In
            </button>
          </div>
        )}

        {/* ---- RESET PASSWORD ---- */}
        {view === "reset" && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 font-heading mb-1">Reset Password</h2>
              <p className="text-sm text-slate-500 font-body">Enter your reset token and a new password.</p>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Reset Token</Label>
                <Input
                  required placeholder="Paste your reset token"
                  value={resetToken} onChange={e => setResetToken(e.target.value)}
                  className="font-mono text-xs"
                  data-testid="reset-token-input"
                />
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"} required minLength={6}
                    placeholder="Min 6 characters"
                    value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    data-testid="reset-new-password-input"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    onClick={() => setShowPassword(v => !v)}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
            <button onClick={() => setView("tabs")} className="text-xs text-slate-400 hover:text-slate-700 transition-colors">
              Back to Sign In
            </button>
          </div>
        )}

        {/* ---- LOGIN / REGISTER TABS ---- */}
        {view === "tabs" && (
          <Tabs defaultValue="login">
            <TabsList className="w-full">
              <TabsTrigger value="login" className="flex-1" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" className="flex-1" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" name="email" type="email" required placeholder="you@example.com" data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <button
                      type="button"
                      onClick={() => setView("forgot")}
                      className="text-xs text-slate-500 hover:text-slate-900 transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input id="login-password" name="password" type="password" required placeholder="Enter password" data-testid="login-password-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="reg-email">Email</Label>
                  <Input id="reg-email" name="email" type="email" required placeholder="you@example.com" data-testid="register-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-username">Username</Label>
                  <Input id="reg-username" name="username" required placeholder="your_username" data-testid="register-username-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Password</Label>
                  <Input id="reg-password" name="password" type="password" required minLength={6} placeholder="Min 6 characters" data-testid="register-password-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                  {loading ? "Creating account..." : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
